import { useAuthentication } from '@ecosystem/newron-client-lib';
import { actions } from '@ecosystem/newron-design-system';
import React, { useCallback, useContext, useEffect, useRef } from 'react';

import { NotificationToastType, QualtricsWorkflowIds, UpsellConstants, UpsellName } from '../../common';
import { useQualtricsAPI } from '../../hooks/useQualtricsAPI';
import { UpsellContext } from '../../state';
import { ActionType, setNotificationMessage } from '../../state/action';
import { handleSurveyButtonClick } from '../../utils/handleSurveyButtonClick';

export type RetailSlideCarouselProps = {
  showRetailSlide: boolean;
};

export const RetailSurveySlide = (): React.ReactElement => {
  const {
    state: { bannerNumber, activeSlideIndex, retailKADUpsellMessage },
    dispatch,
  } = useContext(UpsellContext);

  const { Button } = actions;
  const slideIndex = useRef<number>(activeSlideIndex || 0);
  const bannerId = useRef<number | undefined>(bannerNumber || undefined);
  const { user } = useAuthentication();
  const { triggerQualtricsWorkflow } = useQualtricsAPI();
  const QualtricsSlideIndex = 4;

  useEffect(() => {
    if (bannerNumber !== bannerId.current) {
      bannerId.current = bannerNumber;
    }
  }, [bannerNumber]);

  useEffect(() => {
    if (activeSlideIndex !== slideIndex.current) {
      slideIndex.current = activeSlideIndex;
    }
  }, [activeSlideIndex]);

  const handleClick = useCallback(
    async (response: boolean) => {
      try {
        await handleSurveyButtonClick({
          response,
          user,
          upsellContent: retailKADUpsellMessage?.statement3,
          market: retailKADUpsellMessage?.countryCode + '-' + retailKADUpsellMessage?.itemGroupCode,
          country: user?.country,
          urlTokenId: QualtricsWorkflowIds.RETAIL_KAD_UPSELL,
          upsellType: UpsellName.RETAIL_KAD,
          upsellName: UpsellName.RETAIL_KAD,
          triggerQualtricsWorkflow,
        });
        if (response) {
          dispatch(
            setNotificationMessage({
              message: UpsellConstants.ToastMessageSuccess,
              type: NotificationToastType.Success,
              timeout: 3000,
            })
          );
        }
      } catch (error) {
        dispatch(
          setNotificationMessage({
            message: UpsellConstants.ToastMessageError,
            type: NotificationToastType.Error,
            timeout: 3000,
          })
        );
      }
      dispatch({ type: ActionType.SetActiveSlideIndex, payload: 0 });
      dispatch({ type: ActionType.SetIsModalOpen, payload: false });
    },
    [
      retailKADUpsellMessage?.countryCode,
      retailKADUpsellMessage?.itemGroupCode,
      retailKADUpsellMessage?.statement3,
      triggerQualtricsWorkflow,
      user,
      dispatch,
    ]
  );

  return (
    <div className="retail-kad-qualtrics-slide text-left pt-0 px-8 pb-6" data-test-id="retail-kad-qualtrics-slide">
      <div className="retail-kad-qualtrics-slide-content flex flex-column justify-center">
        <p className="text-center text-xl last-slide-text">
          Learn how
          <span className="font-bold"> Key Account Data </span>
          can enable you to maximise sales together with your key retail accounts. Register your interest today and your
          account manager will be in touch with more information
        </p>
      </div>
      <div className="retail-kad-last-slide-container">
        <p className="text-center text-xl mb-2">
          Register your interest today and your account manager will be in touch with more information
        </p>
        {activeSlideIndex === QualtricsSlideIndex && (
          <div>
            <Button testId="qualtrics-positive" onClick={() => handleClick(true)}>
              Yes I'm interested
            </Button>
            <Button testId="qualtrics-negative" mode="secondary" onClick={() => handleClick(false)}>
              No thanks
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
